import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ErrorNotice from "../components/ErrorNotice";
import getApiBase from "../components/getApiBase";

const ServerErrorNoticeContext = createContext({
  showServerErrorNotice: () => {},
});

export function useServerErrorNotice() {
  return useContext(ServerErrorNoticeContext);
}

function shouldShowForResponse(status, body) {
  if (status < 500) return false;
  if (body && typeof body === "object" && body.reported === true) return true;
  return status >= 500;
}

/**
 * Optional post-checkout Voice open (SSO / status / invite). Callers show a local
 * warning when these fail — do not treat as global TASK-341 "We hit a snag".
 */
function isOptionalVoiceOnboardingUrl(requestUrl) {
  if (!requestUrl || typeof requestUrl !== "string") return false;
  try {
    const path = new URL(requestUrl, "http://local").pathname;
    return (
      /\/api\/ezvoice\/shops\/\d+\/(sso|status|resend-onboarding)\/?$/.test(path) ||
      /\/api\/admin\/v2\/operations\/voice(\/(open|messages))?\/?$/.test(path)
    );
  } catch {
    return (
      /\/api\/ezvoice\/shops\/\d+\/(sso|status|resend-onboarding)/.test(requestUrl) ||
      /\/api\/admin\/v2\/operations\/voice/.test(requestUrl)
    );
  }
}

/**
 * TASK-341 — Global fetch hook: show acknowledgment on API 5xx / reported errors.
 */
export function ServerErrorNoticeProvider({ children }) {
  const [notice, setNotice] = useState({ open: false, referenceId: null });
  const apiBaseRef = useRef("");

  const showServerErrorNotice = useCallback((referenceId) => {
    setNotice({ open: true, referenceId: referenceId || null });
  }, []);

  const handleClose = useCallback(() => {
    setNotice((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    apiBaseRef.current = getApiBase();
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      try {
        const requestUrl =
          typeof args[0] === "string"
            ? args[0]
            : args[0] instanceof Request
              ? args[0].url
              : "";

        if (!requestUrl.startsWith(apiBaseRef.current)) {
          return response;
        }

        if (isOptionalVoiceOnboardingUrl(requestUrl)) {
          return response;
        }

        if (shouldShowForResponse(response.status, null)) {
          const clone = response.clone();
          let body = null;
          try {
            body = await clone.json();
          } catch {
            body = null;
          }

          if (shouldShowForResponse(response.status, body)) {
            const referenceId =
              body && typeof body === "object" && body.referenceId != null
                ? String(body.referenceId)
                : null;
            showServerErrorNotice(referenceId);
          }
        }
      } catch {
        // Never break caller fetch flows
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [showServerErrorNotice]);

  const value = useMemo(
    () => ({ showServerErrorNotice }),
    [showServerErrorNotice],
  );

  return (
    <ServerErrorNoticeContext.Provider value={value}>
      {children}
      <ErrorNotice
        open={notice.open}
        referenceId={notice.referenceId}
        onClose={handleClose}
      />
    </ServerErrorNoticeContext.Provider>
  );
}
